// SPDX-License-Identifier: Elastic-2.0
/**
 * Browser-side MSW handlers for the fixture-mode dev server
 * (`npm run dev:fixture`).
 *
 * Mirrors the smoke spec's `installApiFixtures` (Playwright `page.route`
 * version) but for MSW so a regular Vite dev session can drive the SPA
 * without any backend.
 *
 * URL patterns are wildcard-host so they match whether the SPA calls
 * `http://localhost:8200/devices` (dev default), `/api/mirlai/v1/devices`
 * (single-host add-on), or a future absolute URL — the host part isn't
 * what we're asserting on.
 */
import { HttpResponse, http } from "msw";

import {
  buildLocationDetectHandler,
} from "@/features/onboarding/steps/mocks/locationMock";
import { networkHandlers } from "@/features/onboarding/steps/mocks/networkMock";
import { pairingHandlers } from "@/features/onboarding/steps/mocks/pairingMock";
import { discoveryHandlers } from "@/features/onboarding/steps/mocks/discoveryMock";
import { onboardingHandlers } from "@/features/onboarding/steps/mocks/onboardingMock";
import { matterHandlers } from "@/features/devices/flows/mocks/matterMock";
import { zigbeeHandlers } from "@/features/devices/flows/mocks/zigbeeMock";

import {
  FIXTURE_DEVICES,
  FIXTURE_ENTITLEMENT,
  FIXTURE_ROUTINES,
  FIXTURE_SUGGESTIONS,
  FIXTURE_USER,
} from "./data";

// ⚠️ Every pattern is anchored to the URL origin via the
// `^(?:https?:\/\/[^/]+)?` prefix (origin optional so it works whether
// MSW tests the full href or just the pathname). Without the `^`
// anchor, a resource pattern like `…\/devices\/[^/?]+$` is UNANCHORED
// and also matches a Vite source-module URL such as
// `http://localhost:5173/src/features/devices/useDebouncedEffect.ts`
// (the `/devices/<file>` tail satisfies it). MSW then runs the
// device-detail handler, finds no device with id "useDebouncedEffect.ts"
// and returns 404 — which aborts the dynamic import of any lazy route
// that pulls a direct child of `features/devices/` (e.g. /marketplace),
// surfacing as "Failed to fetch dynamically imported module". Anchoring
// keeps these handlers scoped to real API requests only.
const ORIGIN = String.raw`^(?:https?:\/\/[^/]+)?`;
const API = String.raw`(?:\/api\/mirlai\/v1)?`;

const DEVICES_RE = new RegExp(`${ORIGIN}${API}\\/devices(?:\\?.*)?$`);
const DEVICE_DETAIL_RE = new RegExp(`${ORIGIN}${API}\\/devices\\/[^/?]+(?:\\?.*)?$`);
const DEVICE_CONTROL_RE = new RegExp(`${ORIGIN}${API}\\/devices\\/[^/]+\\/control$`);
const ROUTINES_RE = new RegExp(`${ORIGIN}${API}\\/routines(?:\\?.*)?$`);
const ROUTINE_TRIGGER_RE = new RegExp(`${ORIGIN}${API}\\/routines\\/[^/]+\\/trigger$`);
const SUGGESTIONS_RE = new RegExp(`${ORIGIN}${API}\\/suggestions(?:\\?.*)?$`);
const SUGGESTION_ACTION_RE = new RegExp(
  `${ORIGIN}${API}\\/suggestions\\/[^/]+\\/(approve|dismiss)$`,
);
const ME_RE = new RegExp(`${ORIGIN}\\/me(?:\\?.*)?$`);
const ENTITLEMENT_RE = new RegExp(`${ORIGIN}\\/me\\/entitlement(?:\\?.*)?$`);
const HEALTH_RE = new RegExp(`${ORIGIN}\\/health(?:\\?.*)?$`);
const PAIR_START_RE = new RegExp(`${ORIGIN}\\/auth\\/pair\\/start$`);
const PAIR_VERIFY_RE = new RegExp(`${ORIGIN}\\/auth\\/pair\\/verify$`);
const PAIR_BIND_RE = new RegExp(`${ORIGIN}\\/auth\\/pair\\/bind$`);
const AUTH_REFRESH_RE = new RegExp(`${ORIGIN}\\/auth\\/refresh$`);
const AUTH_LOGOUT_RE = new RegExp(`${ORIGIN}\\/auth\\/logout$`);
const SYSTEM_UPDATES_RE = new RegExp(`${ORIGIN}${API}\\/system\\/updates(?:\\?.*)?$`);

/**
 * Default fixture handler set. Exported so consumers (the dev boot
 * path) can pass them straight to `setupWorker(...handlers)`.
 *
 * Order matters with MSW regex handlers — the more specific patterns
 * (entitlement before /me, device-control before device-detail) go
 * first.
 */
export const fixtureHandlers = [
  // ---- F-200 accounts (regex order: entitlement is /me/entitlement,
  // must match BEFORE plain /me).
  http.get(ENTITLEMENT_RE, () => HttpResponse.json(FIXTURE_ENTITLEMENT)),
  http.get(ME_RE, () => HttpResponse.json(FIXTURE_USER)),

  // ---- F-300 gateway — devices.
  http.post(DEVICE_CONTROL_RE, () =>
    // 202 Accepted with an empty envelope is the contract.
    HttpResponse.json({ accepted: true }, { status: 202 }),
  ),
  http.get(DEVICE_DETAIL_RE, ({ request }) => {
    // Echo back the device by id if known, else a 404.
    const path = new URL(request.url).pathname;
    const id = path.split("/").filter(Boolean).pop() ?? "";
    const device = FIXTURE_DEVICES.devices.find((d) => d.id === id);
    if (!device) {
      return HttpResponse.json(
        { code: "not_found", message: "device not found", recoverable: false },
        { status: 404 },
      );
    }
    return HttpResponse.json(device);
  }),
  http.get(DEVICES_RE, () => HttpResponse.json(FIXTURE_DEVICES)),

  // ---- F-300 gateway — routines.
  http.post(ROUTINE_TRIGGER_RE, () =>
    HttpResponse.json({ triggered: true }, { status: 202 }),
  ),
  http.get(ROUTINES_RE, () => HttpResponse.json(FIXTURE_ROUTINES)),

  // ---- F-300 gateway — suggestions.
  http.post(SUGGESTION_ACTION_RE, ({ request }) => {
    const path = new URL(request.url).pathname;
    const isApprove = path.endsWith("/approve");
    return HttpResponse.json(
      { id: path.split("/").slice(-2, -1)[0], status: isApprove ? "approved" : "dismissed" },
      { status: 202 },
    );
  }),
  http.get(SUGGESTIONS_RE, () => HttpResponse.json(FIXTURE_SUGGESTIONS)),

  // ---- F-300 gateway — health.
  http.get(HEALTH_RE, () =>
    HttpResponse.json({ status: "ok", service: "gateway", version: "fixture", dependencies: {} }),
  ),

  // ---- F-115 system-update check.
  http.get(SYSTEM_UPDATES_RE, () =>
    HttpResponse.json({
      available: false,
      current_version: "fixture",
      latest_version: "fixture",
      changelog: null,
      check_failed: false,
    }),
  ),

  // ---- F-200 auth lifecycle — refresh + logout.
  //      The refresh handler matters even with a far-future fake JWT:
  //      the production refreshOrchestrator now clamps its setTimeout
  //      to 23 days (refreshOrchestrator.ts), so if a fixture session
  //      lives long enough the timer DOES fire — without this handler
  //      the refresh 404s and `auth:reauth-required` boots the user
  //      back to SignIn. Returning the same fake session keeps the
  //      demo idempotent across refresh cycles.
  http.post(AUTH_REFRESH_RE, () =>
    HttpResponse.json({
      access_token: "fixture-cloud-jwt",
      refresh_token: "fixture-refresh",
      expires_in: 900,
      token_type: "Bearer",
    }),
  ),
  http.post(AUTH_LOGOUT_RE, () =>
    HttpResponse.json({ revoked: true }, { status: 204 }),
  ),

  // ---- F-200 pairing — collapse the 3-step OAuth-device-grant
  //      handshake into instant success so the A5 wizard advances on
  //      the first click. `verify` returns the verified envelope
  //      directly (no `pending` polling) so the dev tester doesn't
  //      have to wait. Real-cloud flow tested separately in vitest.
  http.post(PAIR_START_RE, () =>
    HttpResponse.json({
      code: "FIXTURE-1234",
      expires_in: 600,
      verification_uri: "https://accounts.mirlai.local/pair",
    }),
  ),
  http.post(PAIR_VERIFY_RE, () =>
    HttpResponse.json({
      status: "verified",
      access_token: "fixture-cloud-jwt",
      refresh_token: "fixture-refresh",
      expires_in: 900,
      user: {
        user_id: FIXTURE_USER.user_id,
        email: FIXTURE_USER.email,
        display_name: FIXTURE_USER.display_name,
        tier: FIXTURE_USER.tier,
      },
    }),
  ),
  http.post(PAIR_BIND_RE, () =>
    HttpResponse.json({ bound: true, instance_id: "fixture-instance" }),
  ),

  // ---- Onboarding wizard endpoints. These handler bundles already
  //      live next to their respective onboarding steps (test fixture
  //      role); composing them here makes the dev demo walk the full
  //      A1 → A6 flow without falling off the rails at A2 (network
  //      scan) or A5 (cloud pairing).
  ...networkHandlers,
  buildLocationDetectHandler(),
  ...pairingHandlers,
  ...discoveryHandlers,
  // F-352 Matter commissioning. Registered BEFORE onboardingHandlers so the
  // stateful matter job (id "matter-job-1") wins over the generic F-351
  // GET /onboarding/jobs/{id} handler; matterHandlers passthrough for any
  // other job id so the F-351 mock still resolves those.
  ...matterHandlers,
  // F-353 Zigbee pairing. Registered BEFORE onboardingHandlers so the
  // stateful zigbee job (id "zigbee-job-1") wins over the generic F-351
  // GET /onboarding/jobs/{id} handler; zigbeeHandlers passthrough for any
  // other job id so the F-351 mock still resolves those.
  ...zigbeeHandlers,
  ...onboardingHandlers,
];
